import { useState, useRef, useEffect, useMemo } from "react";
import DOMPurify from "dompurify";
import { Button } from "./ui/button";
import { 
  PenLine, 
  Type, 
  Calendar, 
  Hash,
  Move,
  Trash2,
  Save,
  X,
  Plus,
  GripVertical,
  ChevronLeft,
  ChevronRight
} from "lucide-react";

// Field types that can be placed on contracts
const FIELD_TYPES = {
  signature: {
    label: "Signature",
    icon: PenLine,
    color: "bg-blue-500",
    width: 200,
    height: 60
  },
  initials: {
    label: "Initials",
    icon: Type,
    color: "bg-purple-500",
    width: 80,
    height: 40
  },
  date: {
    label: "Date",
    icon: Calendar,
    color: "bg-green-500",
    width: 120,
    height: 30
  },
  text: {
    label: "Text Field",
    icon: Hash,
    color: "bg-orange-500",
    width: 150,
    height: 30
  }
};

// Signer roles with colors
const SIGNER_ROLES = [
  { value: "buyer", label: "Buyer", color: "#3B82F6" },
  { value: "seller", label: "Seller", color: "#10B981" },
  { value: "agent", label: "Agent", color: "#8B5CF6" },
  { value: "witness", label: "Witness", color: "#F59E0B" },
  { value: "other", label: "Other", color: "#6B7280" }
];

export default function SignatureFieldPlacer({ 
  contractContent, 
  pdfUrl,
  totalPages = 1,
  onFieldsChange, 
  initialFields = [],
  readOnly = false 
}) {
  const [fields, setFields] = useState(initialFields);
  const [selectedField, setSelectedField] = useState(null);
  const [dragType, setDragType] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const [activeRole, setActiveRole] = useState("buyer");
  const [selectedFieldType, setSelectedFieldType] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    onFieldsChange?.(fields);
  }, [fields]);

  const handleDragStart = (e, fieldType) => {
    e.dataTransfer.setData("fieldType", fieldType);
    setDragType(fieldType);
  };

  // Click to select field type for placement
  const handleFieldTypeClick = (fieldType) => {
    setSelectedFieldType(selectedFieldType === fieldType ? null : fieldType);
  };

  // Click on contract to place field
  const handleContainerClick = (e) => {
    if (!selectedFieldType || !containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const scrollTop = containerRef.current.scrollTop;
    const scrollLeft = containerRef.current.scrollLeft;
    const x = e.clientX - rect.left + scrollLeft;
    const y = e.clientY - rect.top + scrollTop;
    
    const fieldConfig = FIELD_TYPES[selectedFieldType];
    const newField = {
      id: `field_${Date.now()}`,
      type: selectedFieldType,
      x: Math.max(0, x - fieldConfig.width / 2),
      y: Math.max(0, y - fieldConfig.height / 2),
      width: fieldConfig.width,
      height: fieldConfig.height,
      role: activeRole,
      required: true
    };

    setFields([...fields, newField]);
    setSelectedField(newField.id);
    // Keep field type selected for multiple placements
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const fieldType = e.dataTransfer.getData("fieldType");
    if (!fieldType || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left + containerRef.current.scrollLeft;
    const y = e.clientY - rect.top + containerRef.current.scrollTop;

    const fieldConfig = FIELD_TYPES[fieldType];
    const newField = {
      id: `field_${Date.now()}`,
      type: fieldType,
      x: Math.max(0, x - fieldConfig.width / 2),
      y: Math.max(0, y - fieldConfig.height / 2),
      width: fieldConfig.width,
      height: fieldConfig.height,
      role: activeRole,
      required: true
    };

    setFields([...fields, newField]);
    setSelectedField(newField.id);
    setDragType(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleFieldMouseDown = (e, field) => {
    if (readOnly) return;
    e.stopPropagation();
    setSelectedField(field.id);
    
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const handleFieldDrag = (e, fieldId) => {
    if (readOnly || !containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - dragOffset.x;
    const y = e.clientY - rect.top - dragOffset.y;

    setFields(fields.map(f => 
      f.id === fieldId 
        ? { ...f, x: Math.max(0, x), y: Math.max(0, y) }
        : f
    ));
  };

  const deleteField = (fieldId) => {
    setFields(fields.filter(f => f.id !== fieldId));
    if (selectedField === fieldId) setSelectedField(null);
  };

  const updateFieldRole = (fieldId, role) => {
    setFields(fields.map(f => 
      f.id === fieldId ? { ...f, role } : f
    ));
  };

  const getRoleColor = (role) => {
    return SIGNER_ROLES.find(r => r.value === role)?.color || "#6B7280";
  };

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      {!readOnly && (
        <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border-b border-white/[0.08] p-3">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-white">Place Signature Fields</h3>
            <div className="flex items-center gap-3">
              {/* PDF Page Navigation */}
              {pdfUrl && totalPages > 1 && (
                <div className="flex items-center gap-1 bg-white/[0.06] border border-white/[0.1] rounded px-2 py-1">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="text-white/50 hover:text-white disabled:opacity-30 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-white/70 text-xs px-1.5">
                    Page {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage >= totalPages}
                    className="text-white/50 hover:text-white disabled:opacity-30 transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
              <span className="text-xs text-white/50">Assign to:</span>
              <select
                value={activeRole}
                onChange={(e) => setActiveRole(e.target.value)}
                className="bg-white/[0.08] border border-white/[0.1] text-white text-xs rounded px-2 py-1"
              >
                {SIGNER_ROLES.map(role => (
                  <option key={role.value} value={role.value}>{role.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Field Type Buttons */}
          <div className="flex gap-2">
            {Object.entries(FIELD_TYPES).map(([key, config]) => (
              <button
                key={key}
                draggable
                onDragStart={(e) => handleDragStart(e, key)}
                onClick={() => handleFieldTypeClick(key)}
                className={`flex items-center gap-2 px-3 py-2 ${config.color} text-white text-xs font-semibold rounded cursor-grab hover:opacity-90 transition-all shadow-md ${
                  selectedFieldType === key ? 'ring-2 ring-white ring-offset-2 ring-offset-black scale-105' : ''
                }`}
                style={{ borderLeft: `4px solid ${getRoleColor(activeRole)}` }}
              >
                <config.icon className="w-4 h-4" />
                {config.label}
              </button>
            ))}
          </div>
          
          <p className="text-xs text-white/40 mt-2">
            {selectedFieldType 
              ? `Click on the contract to place a ${FIELD_TYPES[selectedFieldType].label}. Click button again to deselect.`
              : 'Click a button to select, then click on contract to place. Or drag and drop.'
            }
          </p>
        </div>
      )}

      {/* Contract Display with Droppable Area */}
      <div 
        ref={containerRef}
        className={`flex-1 relative bg-white overflow-auto ${selectedFieldType ? 'cursor-crosshair' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={handleContainerClick}
        style={{ minHeight: "600px" }}
      >
        {/* PDF background (pointer-events:none so clicks pass through to container) */}
        {pdfUrl ? (
          <iframe
            src={`${pdfUrl}#page=${currentPage}&toolbar=0&navpanes=0`}
            title="Document Preview"
            style={{
              width: "100%",
              height: "1400px",
              border: "none",
              pointerEvents: "none",
              display: "block"
            }}
          />
        ) : (
          /* HTML/text content - sanitized to prevent XSS */
          <div 
            className="p-8 prose prose-sm max-w-none text-black prose-headings:text-black prose-p:text-gray-800 prose-li:text-gray-800"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(contractContent || '') }}
          />
        )}

        {/* Placed Fields Overlay */}
        {fields.map((field) => {
          const config = FIELD_TYPES[field.type];
          const roleColor = getRoleColor(field.role);
          const isSelected = selectedField === field.id;
          
          return (
            <div
              key={field.id}
              className={`absolute cursor-move transition-shadow ${
                isSelected ? 'ring-2 ring-gold shadow-lg' : 'hover:ring-1 hover:ring-white/50'
              }`}
              style={{
                left: field.x,
                top: field.y,
                width: field.width,
                height: field.height,
                backgroundColor: `${roleColor}20`,
                border: `2px dashed ${roleColor}`,
                borderRadius: '4px'
              }}
              onMouseDown={(e) => handleFieldMouseDown(e, field)}
              onDrag={(e) => handleFieldDrag(e, field.id)}
              draggable={!readOnly}
            >
              {/* Field Content */}
              <div className="flex items-center justify-center h-full">
                <div className="flex items-center gap-1 text-xs" style={{ color: roleColor }}>
                  <config.icon className="w-3 h-3" />
                  <span className="font-medium">{config.label}</span>
                </div>
              </div>

              {/* Role Badge */}
              <div 
                className="absolute -top-5 left-0 px-1.5 py-0.5 text-[10px] font-bold text-white rounded-t"
                style={{ backgroundColor: roleColor }}
              >
                {field.role.toUpperCase()}
              </div>

              {/* Delete Button (when selected) */}
              {isSelected && !readOnly && (
                <button
                  onClick={() => deleteField(field.id)}
                  className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                >
                  <X className="w-3 h-3" />
                </button>
              )}

              {/* Resize Handle */}
              {isSelected && !readOnly && (
                <div className="absolute bottom-0 right-0 w-3 h-3 bg-white/40 cursor-se-resize rounded-bl" />
              )}
            </div>
          );
        })}
      </div>

      {/* Field Summary */}
      {fields.length > 0 && (
        <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border-t border-white/[0.08] p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-xs text-white/50">
                {fields.length} field{fields.length !== 1 ? 's' : ''} placed
              </span>
              <div className="flex gap-2">
                {SIGNER_ROLES.map(role => {
                  const count = fields.filter(f => f.role === role.value).length;
                  if (count === 0) return null;
                  return (
                    <span 
                      key={role.value}
                      className="text-xs px-2 py-0.5 rounded"
                      style={{ 
                        backgroundColor: `${role.color}20`,
                        color: role.color
                      }}
                    >
                      {role.label}: {count}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export { FIELD_TYPES, SIGNER_ROLES };
